import {
  MutableRefObject, useRef, useState, useCallback,
} from 'react'

export type SheetState = 'half' | 'full'

interface UseBottomSheetDragOptions {
  onClose?: () => void
  onSheetStateChange?: (state: SheetState) => void
  isKeyboardOpenRef?: MutableRefObject<boolean>
}

/**
 * Custom hook for bottom sheet drag interaction.
 * Supports drag down to close/collapse and drag up to expand.
 *
 * States:
 *  - 'half'  = default open (fills space below anchor point)
 *  - 'full'  = expanded (nearly full screen)
 *
 * Drag gestures:
 *  - Drag down from 'full'  -> collapse to 'half'
 *  - Drag down from 'half'  -> close (via onClose callback)
 *  - Drag up   from 'half'  -> expand to 'full'
 *
 * When isKeyboardOpenRef.current is true, `top` transitions are disabled
 * to prevent iOS Safari from scrolling the visual viewport during state
 * changes. The `top` property snaps instantly while the keyboard is open.
 */
export const useBottomSheetDrag = ({
  onClose,
  onSheetStateChange,
  isKeyboardOpenRef,
}: UseBottomSheetDragOptions = {}) => {
  const [sheetState, setSheetState] = useState<SheetState>('half')
  const startY = useRef(0)
  const currentY = useRef(0)
  const sheetRef = useRef<HTMLDivElement>(null)

  const updateSheetState = useCallback((newState: SheetState) => {
    setSheetState(newState)
    onSheetStateChange?.(newState)
  }, [onSheetStateChange])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!e.touches.length) return
    startY.current = e.touches[0].clientY
    currentY.current = e.touches[0].clientY
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!e.touches.length) return
    // Prevent iOS from interpreting the drag gesture as a page scroll.
    // Without this, dragging up to expand while the keyboard is open
    // causes the document to scroll, shifting the header and video.
    e.preventDefault()
    currentY.current = e.touches[0].clientY
    const delta = currentY.current - startY.current
    if (sheetRef.current && delta > 0) {
      // Only allow visual drag downward
      sheetRef.current.style.transform = `translateY(${delta}px)`
    }
  }, [])

  const handleTouchEnd = useCallback(() => {
    const delta = currentY.current - startY.current
    if (delta > 100) {
      if (sheetState === 'full') {
        // Dragged down from full -> collapse to half
        updateSheetState('half')
      } else if (onClose) {
        // Dragged down from half -> close.
        // Blur and clear bottom BEFORE triggering close so the keyboard
        // dismisses in sync with the drawer slide-down animation.
        // Without this, the bottom resets (drawer grows taller) while
        // the drawer is sliding off, flashing the page underneath.
        const active = document.activeElement as HTMLElement | null
        if (active && sheetRef.current?.contains(active)) {
          active.blur()
        }
        if (sheetRef.current) {
          sheetRef.current.style.bottom = ''
        }
        onClose()
      }
    } else if (delta < -100 && sheetState === 'half') {
      // Dragged up from half -> expand to full
      updateSheetState('full')
    }
    // Reset inline transform immediately (no CSS transition).
    // Without this, the CSS `transition: transform` would animate from
    // the drag offset back to 0, conflicting with the `top` transition
    // during full→half and creating a visual glitch.
    if (sheetRef.current) {
      const el = sheetRef.current
      const kbOpen = isKeyboardOpenRef?.current

      // When keyboard is open, exclude `top` from transitions entirely.
      // Animating `top` while an input is focused causes iOS to scroll
      // the visual viewport to track the focused element, shifting the
      // header and video off-screen. Snapping `top` is instant — iOS
      // doesn't get animation frames to trigger a scroll.
      el.style.transition = kbOpen ? 'none' : 'top 0.3s ease-in-out'
      el.style.transform = ''
      // Force reflow so the transform reset takes effect without animation,
      // then restore transitions for future animations.
      el.getClientRects()
      el.style.transition = kbOpen
        ? 'transform 0.3s ease-in-out'
        : ''
    }
  }, [onClose, sheetState, updateSheetState, isKeyboardOpenRef])

  // Reset to half state (e.g., when drawer reopens)
  const resetSheetState = useCallback(() => {
    setSheetState('half')
  }, [])

  return {
    sheetRef,
    sheetState,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetSheetState,
  }
}
